import { useState } from 'react';
import Form from '../builder/Form';
import { DataCaptureTabProps, OutcomeDefinition } from './types';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import AgentTabHeader from './AgentTabHeader';

function nextOutcomeName(outcomes: OutcomeDefinition[]): string {
  const names = new Set(outcomes.map((outcome) => outcome.name));
  let index = outcomes.length + 1;
  while (names.has(`outcome_${index}`)) {
    index += 1;
  }
  return `outcome_${index}`;
}

type OutcomeModalState =
  | {
      mode: 'create';
      outcome: OutcomeDefinition;
    }
  | {
      mode: 'edit';
      index: number;
      outcome: OutcomeDefinition;
    }
  | null;

export default function DataCaptureTab({
  agId,
  agentName,
  form,
  setForm,
  outcomes,
  setOutcomes,
  enableTracks,
  trackNodes,
  setTrackNodes,
  actions,
  setActions,
  qualifyVisitor,
  setQualifyVisitor,
  setNeedsUpdate,
  workEmailOnly,
  setWorkEmailOnly,
  requireContactInfo,
  setRequireContactInfo,
  messagesBeforeContactInfo,
  setMessagesBeforeContactInfo,
  hideContactUsButton,
  setHideContactUsButton
}: DataCaptureTabProps) {
  const normalizedOutcomes = Array.isArray(outcomes) ? outcomes : [];
  const [outcomeModal, setOutcomeModal] = useState<OutcomeModalState>(null);

  function saveOutcome(index: number, updatedOutcome: OutcomeDefinition) {
    const next = normalizedOutcomes.map((outcome, i) => (
      i === index ? { ...updatedOutcome, sort_order: updatedOutcome.sort_order ?? i } : outcome
    ));
    setOutcomes(next);
    setNeedsUpdate(true);
  }

  function addOutcome(outcome: OutcomeDefinition) {
    setOutcomes([
      ...normalizedOutcomes,
      {
        ...outcome,
        sort_order: normalizedOutcomes.length,
      },
    ]);
    setNeedsUpdate(true);
  }

  function openCreateOutcomeModal() {
    setOutcomeModal({
      mode: 'create',
      outcome: {
        name: nextOutcomeName(normalizedOutcomes),
        label: '',
        description: '',
        active: true,
        sort_order: normalizedOutcomes.length,
      },
    });
  }

  function openEditOutcomeModal(index: number) {
    const outcome = normalizedOutcomes[index];
    if (!outcome) return;
    setOutcomeModal({
      mode: 'edit',
      index,
      outcome: {
        ...outcome,
        active: outcome.active !== false,
        description: outcome.description || '',
      },
    });
  }

  function submitOutcomeModal(outcome: OutcomeDefinition) {
    if (!outcome.label.trim()) {
      alert('Outcome name is required');
      return;
    }

    const normalizedOutcome = {
      ...outcome,
      label: outcome.label.trim(),
      description: outcome.description?.trim() || '',
      active: outcome.active !== false,
    };

    if (outcomeModal?.mode === 'edit') {
      saveOutcome(outcomeModal.index, normalizedOutcome);
    } else {
      addOutcome(normalizedOutcome);
    }
    setOutcomeModal(null);
  }

  function deleteOutcome(index: number) {
    setOutcomes(normalizedOutcomes.filter((_, i) => i !== index));
    setNeedsUpdate(true);
  }

  return (
    <div className="w-full">
      <AgentTabHeader
        icon={FileText}
        title="Data Capture"
        description="Define the information your agent collects through conversation and forms."
      />

      <div className="mx-auto w-full max-w-4xl space-y-8 py-6">
      {/* Form Builder */}
      <Form
        agId={agId}
        agentName={agentName}
        form={form}
        setForm={setForm}
        setNeedsUpdate={setNeedsUpdate}
        variant="standard"
        trackNodes={trackNodes}
        setTrackNodes={setTrackNodes}
        actions={actions}
        setActions={setActions}
        qualifyVisitor={qualifyVisitor}
        setQualifyVisitor={setQualifyVisitor}
        workEmailOnly={workEmailOnly}
        setWorkEmailOnly={setWorkEmailOnly}
        requireContactInfo={requireContactInfo}
        setRequireContactInfo={setRequireContactInfo}
        messagesBeforeContactInfo={messagesBeforeContactInfo}
        setMessagesBeforeContactInfo={setMessagesBeforeContactInfo}
        hideContactUsButton={hideContactUsButton}
        setHideContactUsButton={setHideContactUsButton}
      />

      <section className="space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold">Conversation outcomes</h3>
            <p className="mt-1 text-xs leading-5 text-muted-foreground">
              Define the end-state assigned after a conversation becomes inactive.
            </p>
          </div>
            <Button size="sm" variant="outline" onClick={openCreateOutcomeModal}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add outcome
            </Button>
        </div>

        <div className="overflow-hidden rounded-lg border bg-card divide-y">
          {normalizedOutcomes.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm font-medium">No outcomes yet</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Add outcomes like “Meeting booked”, “Sent to support”, or “No clear outcome”.
              </p>
            </div>
          ) : (
            normalizedOutcomes.map((outcome, index) => (
              <div key={outcome.name || index} className="p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium truncate">{outcome.label || 'Untitled outcome'}</p>
                      <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        {outcome.active === false ? 'Inactive' : 'Active'}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      System name: <span className="font-mono">{outcome.name}</span>
                    </p>
                    {outcome.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 pt-1">
                        {outcome.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground"
                      onClick={() => openEditOutcomeModal(index)}
                      aria-label={`Edit ${outcome.label || 'outcome'}`}
                    >
                      <Pencil className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteOutcome(index)}
                      aria-label={`Delete ${outcome.label || 'outcome'}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {outcomeModal && (
        <OutcomeModal
          mode={outcomeModal.mode}
          outcome={outcomeModal.outcome}
          onClose={() => setOutcomeModal(null)}
          onSubmit={submitOutcomeModal}
        />
      )}
      </div>
    </div>
  );
}

function OutcomeModal({
  mode,
  outcome,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  outcome: OutcomeDefinition;
  onClose: () => void;
  onSubmit: (outcome: OutcomeDefinition) => void;
}) {
  const [draft, setDraft] = useState<OutcomeDefinition>(() => ({
    ...outcome,
    label: outcome.label || '',
    description: outcome.description || '',
    active: outcome.active !== false,
  }));

  function updateDraft(updates: Partial<OutcomeDefinition>) {
    setDraft((current) => ({ ...current, ...updates }));
  }

  return (
    <Dialog open onOpenChange={(open) => {
      if (!open) onClose();
    }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Edit Outcome' : 'Create Outcome'}</DialogTitle>
          <DialogDescription>
            Define when the AI should assign this conversation outcome after a session becomes inactive.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="space-y-2">
            <Label htmlFor="outcomeLabel">Outcome Name</Label>
            <Input
              id="outcomeLabel"
              value={draft.label}
              onChange={(event) => updateDraft({ label: event.target.value })}
              placeholder="e.g., Sent to support"
              autoFocus
            />
            <p className="text-xs text-muted-foreground">
              System name: <span className="font-mono">{draft.name}</span>
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="outcomeDescription">Classification Instructions</Label>
            <Textarea
              id="outcomeDescription"
              value={draft.description || ''}
              onChange={(event) => updateDraft({ description: event.target.value })}
              placeholder="Tell the AI when this outcome should be selected."
              rows={4}
            />
          </div>

          <div className="flex items-center justify-between rounded-lg border bg-muted/30 p-3">
            <div>
              <Label className="text-sm font-medium">Active</Label>
              <p className="text-xs text-muted-foreground">
                Inactive outcomes are saved but ignored during classification.
              </p>
            </div>
            <Switch
              checked={draft.active !== false}
              onCheckedChange={(checked) => updateDraft({ active: checked })}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onSubmit(draft)}>
            {mode === 'edit' ? 'Save Changes' : 'Create Outcome'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

